import type {
	AutocompleteResult,
	City,
	Condo,
	LocationProviders,
	PointsOfInterest,
	Region,
	Street
} from '~~/core/ports/repositories/LocationAutocomplete';
import type { ShallowRef } from 'vue';

import { useDebounceFn } from '@vueuse/core';
import { useCityRegistry } from '~/composables/useCityRegistry';
import { SEARCH_SUGGEST } from '~/constants';
import { shallowRef, watch } from 'vue';

import { getMacroregions } from '~/components/SearchSuggest';
import { mapMacroregionToState } from '~/components/SearchSuggest/mappings';

import CondosGroupIcon from '~icons/material-symbols-light/corporate-fare';
import MacroregionsGroupIcon from '~icons/material-symbols-light/explore-outline';
import RegionsGroupIcon from '~icons/material-symbols-light/home-work-outline';
import CitiesGroupIcon from '~icons/material-symbols-light/map-outline';
import StreetsGroupIcon from '~icons/material-symbols-light/map-outline';
import PointsOfInterestGroupIcon from '~icons/material-symbols-light/where-to-vote-outline';

export interface SuggestionResult {
	title: string;
	subtitle: string;
}

export interface SearchParams {
	regions?: string[];
	cities?: string[];
	macroregions?: string[];
	placeId?: string | null;
	condoSlug?: string | null;
	slug?: string;
	code?: string | null;
}

export interface Suggestion {
	id: string;
	result: SuggestionResult;
	searchParams: SearchParams;
	searchType?: string;
	type?: string;
	macroregionUsed?: string;
}

export interface SuggestionGroup {
	heading: {
		text: string;
		icon: any;
	};
	key: SuggestionGroupKey;
	visible: boolean;
	suggestions: Suggestion[];
}

export interface AutocompleteMaxNumberLocationOptions {
	cities?: number;
	regions?: number;
	streets?: number;
	pointsOfInterest?: number;
	condos?: number;
}

type SuggestionGroupKey =
	| 'macroregions'
	| 'cities'
	| 'regions'
	| 'streets'
	| 'pointsOfInterest'
	| 'condos'
	| 'code'
	| 'lastResearchs';

const suggestionGroupLabel = {
	macroregions: 'Regiões',
	cities: 'Cidades',
	regions: 'Bairros',
	streets: 'Endereços',
	pointsOfInterest: 'Pontos de interesse',
	condos: 'Condomínios'
} as const;

const iconMap = {
	macroregions: MacroregionsGroupIcon,
	cities: CitiesGroupIcon,
	regions: RegionsGroupIcon,
	streets: StreetsGroupIcon,
	pointsOfInterest: PointsOfInterestGroupIcon,
	condos: CondosGroupIcon
} as const;

function extractStateFromSubtitle(subtitle: string): string | undefined {
	const parts = subtitle.split('-').map(p => p.trim());
	const lastPart = parts[parts.length - 1];
	if (lastPart && lastPart.length === 2 && lastPart.match(/^[A-Z]{2}$/)) {
		return lastPart;
	}
	return undefined;
}
export interface UseLocationAutocompleteOptions {
	providers?: LocationProviders[];
	hasSelectedValue?: ShallowRef<boolean>;
	isDropdownOpen?: Ref<boolean>;
	maxNumber?: AutocompleteMaxNumberLocationOptions;
	macroregion?: Ref<string | undefined> | ComputedRef<string | undefined>;
}

export const useLocationAutocomplete = (
	searchTerm: ShallowRef<string>,
	options?: UseLocationAutocompleteOptions
) => {
	const { getStaticSuggestions } = useCityRegistry();
	const lastExecutedTerm = shallowRef<string>('');
	const filteredProviders = computed(() =>
		options?.providers?.filter(p => p !== 'macroregions')
	);
	const macroregionRef = computed(() => toValue(options?.macroregion));

	const apiData = ref<AutocompleteResult | null>(null);
	const pending = ref(false);
	const error = ref<Error | null>(null);

	function sanitizeSearchTerm(value: string | undefined): string {
		return (value || '').trim();
	}

	async function executeLocationAutocomplete(opts?: {
		force?: boolean;
		term?: string;
	}) {
		if (opts?.force) {
			cancelDebouncedExecute();
		}

		// Usa o termo passado ou lê do searchTerm (para chamadas diretas/force)
		const term = opts?.term ?? sanitizeSearchTerm(searchTerm.value);
		if (term.length < SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
			cancelDebouncedExecute();
			lastExecutedTerm.value = '';
			apiData.value = null;
			return;
		}

		if (!opts?.force && term === lastExecutedTerm.value) {
			return;
		}

		lastExecutedTerm.value = term;
		pending.value = true;
		error.value = null;

		try {
			apiData.value = await $fetch<AutocompleteResult | null>(
				'/api/location/autocomplete',
				{
					query: {
						searchTerm: term,
						providers: filteredProviders.value,
						macroregion: macroregionRef.value
					}
				}
			);
		} catch (err) {
			error.value = err as Error;
			console.error('Location autocomplete error:', err);
		} finally {
			pending.value = false;
		}
	}

	// Captura o termo no momento do watcher, não no momento da execução
	let pendingTerm = '';

	const debouncedExecute = useDebounceFn(() => {
		if (
			!options?.hasSelectedValue?.value &&
			(options?.isDropdownOpen?.value ?? true) &&
			pendingTerm.length >= SEARCH_SUGGEST.MIN_SEARCH_LENGTH
		) {
			// Usa o termo capturado, não o valor atual do searchTerm
			executeLocationAutocomplete({ term: pendingTerm });
		}
	}, SEARCH_SUGGEST.TYPING_DEBOUNCE_MS);

	function cancelDebouncedExecute() {
		pendingTerm = '';
		const cancel = (debouncedExecute as { cancel?: () => void }).cancel;
		if (typeof cancel === 'function') {
			cancel();
		}
	}

	watch(searchTerm, () => {
		const term = sanitizeSearchTerm(searchTerm.value);
		if (term.length < SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
			lastExecutedTerm.value = '';
			apiData.value = null;
			cancelDebouncedExecute();
			return;
		}

		// Captura o termo AGORA, antes do debounce
		pendingTerm = term;
		debouncedExecute();
	});

	const availableMacroregions = getMacroregions();

	const {
		cities: maxNumberOfCities = 3,
		regions: maxNumberOfRegions = 3,
		streets: maxNumberOfStreets = 4,
		pointsOfInterest: maxNumberOfPointsOfInterest = 4,
		condos: maxNumberOfCondos = 3
	} = options?.maxNumber || {};

	const data = computed(() => {
		if (searchTerm.value.length < SEARCH_SUGGEST.MIN_SEARCH_LENGTH) {
			return [
				{
					heading: {
						text: suggestionGroupLabel.macroregions,
						icon: iconMap.macroregions
					},
					key: 'macroregions' as SuggestionGroupKey,
					visible: true,
					suggestions: availableMacroregions
						.filter(m => m.available !== false)
						.map(macroregion => ({
							id: macroregion.id,
							result: {
								title: macroregion.result.title,
								subtitle: macroregion.result.subtitle
							},
							searchParams: macroregion.searchParams,
							searchType: macroregion.searchType,
							macroregionUsed: macroregion.macroregionUsed
						}))
				}
			];
		}

		let finalApiData = apiData.value;

		// Função helper para remover duplicatas baseado em title e subtitle
		const deduplicateByTitleSubtitle = <
			T extends { result: { title: string; subtitle: string } }
		>(
			items: T[]
		): T[] => {
			const seen = new Set<string>();
			return items.filter(item => {
				const key = `${item.result.title}|${item.result.subtitle}`;
				if (seen.has(key)) {
					return false;
				}
				seen.add(key);
				return true;
			});
		};

		// Verifica se há sugestões estáticas para esta busca (qualquer cidade registrada)
		const staticSuggestions = getStaticSuggestions(searchTerm.value);

		if (staticSuggestions && apiData.value) {
			// Mescla dados da API com sugestões estáticas da cidade
			finalApiData = {
				regions: deduplicateByTitleSubtitle<Region>([
					...(apiData.value?.regions || []),
					...staticSuggestions.regions.map((region, idx) => ({
						id: `static-region-${idx}`,
						timeToExecute: 0,
						searchType: 'region' as const,
						result: {
							title: region.label,
							subtitle: `${region.data.city} - ${region.data.macroregion}`
						},
						searchParams: {
							regions: [region.label],
							cities: [region.data.city],
							macroregions: [region.data.macroregion],
							placeId: '',
							condoSlug: null
						}
					}))
				]),
				cities: deduplicateByTitleSubtitle<City>([
					...(apiData.value?.cities || []),
					...staticSuggestions.cities.map((city, idx) => ({
						id: `static-city-${idx}`,
						timeToExecute: 0,
						searchType: 'city' as const,
						result: {
							title: city.label,
							subtitle: city.state
						},
						searchParams: {
							regions: [],
							cities: [city.label],
							macroregions: [],
							placeId: '',
							condoSlug: null
						}
					}))
				]),
				streets: deduplicateByTitleSubtitle<Street>([
					...(apiData.value?.streets || []),
					...staticSuggestions.streets.map((street, idx) => ({
						id: `static-street-${idx}`,
						timeToExecute: 0,
						searchType: 'street' as const,
						result: {
							title: street.label,
							subtitle: `${street.city} - ${street.state}`
						},
						searchParams: {
							regions: [],
							cities: [street.city],
							macroregions: [],
							placeId: '',
							condoSlug: null
						}
					}))
				]),
				pointsOfInterest: deduplicateByTitleSubtitle<PointsOfInterest>([
					...(apiData.value?.pointsOfInterest || []),
					...staticSuggestions.pointsOfInterest.map((poi, idx) => ({
						id: `static-poi-${idx}`,
						timeToExecute: 0,
						searchType: 'point_of_interest' as const,
						result: {
							title: poi.label,
							subtitle: `${poi.city} - ${poi.state}`
						},
						searchParams: {
							regions: [],
							cities: [poi.city],
							macroregions: [],
							placeId: '',
							condoSlug: null
						}
					}))
				]),
				condos: deduplicateByTitleSubtitle<Condo>([
					...(apiData.value?.condos || []),
					...staticSuggestions.condos.map((condo, idx) => ({
						id: `static-condo-${idx}`,
						timeToExecute: 0,
						searchType: 'condo' as const,
						result: {
							title: condo.label,
							subtitle: `${condo.city} - ${condo.state}`
						},
						searchParams: {
							regions: [],
							cities: [condo.city],
							macroregions: [],
							placeId: '',
							condoSlug: null
						}
					}))
				]),
				macroregionUsed: apiData.value?.macroregionUsed || '',
				providersUsed: apiData.value?.providersUsed || []
			};
		}

		if (!finalApiData) {
			return [];
		}

		const transformedData: SuggestionGroup[] = [
			{
				heading: {
					text: suggestionGroupLabel.macroregions,
					icon: iconMap.macroregions
				},
				key: 'macroregions',
				visible: false,
				suggestions: []
			},
			{
				heading: {
					text: suggestionGroupLabel.cities,
					icon: iconMap.cities
				},
				key: 'cities',
				visible: finalApiData.cities.length > 0,
				suggestions: finalApiData.cities
					.slice(0, maxNumberOfCities)
					.map((city: City) => {
						const extractedState = extractStateFromSubtitle(
							city.result.subtitle
						);
						// Prioriza o estado extraído do subtitle, depois tenta macroregion
						const state =
							extractedState ||
							mapMacroregionToState(city.searchParams.macroregions?.[0]);
						return {
							id: city.id,
							result: {
								title: city.result.title,
								subtitle: city.result.subtitle
							},
							searchParams: {
								...city.searchParams,
								states: [state]
							},
							searchType: city.searchType,
							macroregionUsed: finalApiData.macroregionUsed
						};
					})
			},
			{
				heading: {
					text: suggestionGroupLabel.regions,
					icon: iconMap.regions
				},
				key: 'regions',
				visible: finalApiData.regions.length > 0,
				suggestions: finalApiData.regions
					.slice(0, maxNumberOfRegions)
					.map((region: Region) => {
						const extractedState = extractStateFromSubtitle(
							region.result.subtitle
						);
						// Prioriza o estado extraído do subtitle, depois tenta macroregion
						const state =
							extractedState ||
							mapMacroregionToState(region.searchParams.macroregions?.[0]);
						return {
							id: region.id,
							result: {
								title: region.result.title,
								subtitle: region.result.subtitle
							},
							searchParams: {
								...region.searchParams,
								states: [state]
							},
							searchType: region.searchType,
							macroregionUsed: finalApiData.macroregionUsed
						};
					})
			},
			{
				heading: {
					text: suggestionGroupLabel.streets,
					icon: iconMap.streets
				},
				key: 'streets',
				visible: finalApiData.streets.length > 0,
				suggestions: finalApiData.streets
					.slice(0, maxNumberOfStreets)
					.map((street: Street) => {
						const extractedState = extractStateFromSubtitle(
							street.result.subtitle
						);
						return {
							id: street.id,
							result: {
								title: street.result.title,
								subtitle: street.result.subtitle
							},
							searchParams: {
								...street.searchParams,
								states: extractedState ? [extractedState] : undefined
							},
							searchType: street.searchType,
							macroregionUsed: finalApiData.macroregionUsed
						};
					})
			},
			{
				heading: {
					text: suggestionGroupLabel.pointsOfInterest,
					icon: iconMap.pointsOfInterest
				},
				key: 'pointsOfInterest',
				visible: finalApiData.pointsOfInterest.length > 0,
				suggestions: finalApiData.pointsOfInterest
					.slice(0, maxNumberOfPointsOfInterest)
					.map((poi: PointsOfInterest) => {
						const extractedState = extractStateFromSubtitle(
							poi.result.subtitle
						);
						return {
							id: poi.id,
							result: {
								title: poi.result.title,
								subtitle: poi.result.subtitle
							},
							searchParams: {
								...poi.searchParams,
								states: extractedState ? [extractedState] : undefined
							},
							searchType: 'point_of_interest',
							macroregionUsed: finalApiData.macroregionUsed
						};
					})
			},
			{
				heading: {
					text: suggestionGroupLabel.condos,
					icon: iconMap.condos
				},
				key: 'condos',
				visible: finalApiData.condos.length > 0,
				suggestions: finalApiData.condos
					.slice(0, maxNumberOfCondos)
					.map((condo: Condo) => {
						const extractedState = extractStateFromSubtitle(
							condo.result.subtitle
						);
						return {
							id: condo.id,
							result: {
								title: condo.result.title,
								subtitle: condo.result.subtitle
							},
							searchParams: {
								...condo.searchParams,
								states: extractedState ? [extractedState] : undefined,
								slug: condo.searchParams.condoSlug || undefined
							},
							searchType: 'condo',
							macroregionUsed: finalApiData.macroregionUsed
						};
					})
			}
		];

		return transformedData.filter(
			group => group.visible && group.suggestions.length
		);
	});

	const loading = computed(() => pending.value);

	return {
		data,
		loading,
		error,
		execute: executeLocationAutocomplete
	};
};
